/* eslint-disable no-nested-ternary */
import {
  useState, useRef, useEffect, forwardRef, useReducer, useImperativeHandle,
} from 'react';
import { nanoid } from 'nanoid';
import { Range, getTrackBackground } from 'react-range';
import { useSearchZustandStore } from '@/helpers/searchctx.js';
import Icon from '@/core/icon.js';
import { useTranslation } from '@/helpers/translate';
import { debounceFunction, useDebounce } from '@/helpers/common';

const RangeDetail = forwardRef(({
  data, params, onChange,
}, ref) => {
  const refInputMin = useRef();
  const refInputMax = useRef();
  const { t } = useTranslation();
  const [value, setValue] = useState(data);

  const {
    minTitle, maxTitle, unit,
  } = params || {};

  useImperativeHandle(ref, () => ({
    setValue: () => {
      const [valueMin, valueMax] = value;
      return [valueMin, valueMax];
    },
  }), [value]);

  const validateAndCorrect = (from, newValue) => {
    const [valueMin, valueMax] = newValue;
    if (from === 'min' && valueMin > valueMax) {
      setValue([valueMax, valueMax, nanoid()]);
      onChange([valueMax, valueMax]);
    } else if (from === 'max' && valueMax < valueMin) {
      setValue([valueMin, valueMin, nanoid()]);
      onChange([valueMin, valueMin]);
    } else {
      setValue([valueMin, valueMax, nanoid()]);
      onChange([valueMin, valueMax]);
    }
  };

  useEffect(() => {
    const [valueMin, valueMax, isChanging] = value;
    // console.log('valueMin, valueMax, isChanging', valueMin, valueMax, isChanging);
    if (isChanging) {
      if (refInputMin?.current) {
        refInputMin.current.value = Number.isNaN(valueMin) ? '' : valueMin;
      }
      if (refInputMax?.current) {
        refInputMax.current.value = Number.isNaN(valueMax) ? '' : valueMax;
      }
    }
  }, [value]);

  useEffect(() => {
    if (data) {
      setValue([...data, nanoid()]);
    }
  }, [data]);

  return (
    <div className="flex gap-x-2 justify-center mt-3">
      <div className="flex flex-col w-full">
        <span className="pb-1 text-xs text-dark">{t(minTitle)}</span>
        <div className="flex items-center w-full">
          <input
            ref={refInputMin}
            type="number"
            defaultValue={value[0]}
            onBlur={(e) => {
              const [currentMin, currentMax] = value;
              validateAndCorrect('min', [parseInt(e.target.value, 10), currentMax]);
              // handleInputChange(0, parseInt(e.target.value, 10));
            }}
            className="px-3 py-2 w-full text-black rounded-lg border border-purple-4/40 focus:ring-2 focus:ring-purple focus:ring-offset-0 focus:outline-none focus:border-transparent"
          />
          <span className="relative right-6 text-purple-6/50">{unit}</span>
        </div>
      </div>
      <div className="flex flex-col w-full">
        <span className="pb-1 text-xs text-dark">{t(maxTitle)}</span>
        <div className="flex items-center">
          <input
            ref={refInputMax}
            type="number"
            defaultValue={value[1]}
            onBlur={(e) => {
              const [currentMin, currentMax] = value;
              validateAndCorrect('max', [currentMin, parseInt(e.target.value, 10)]);
            }}
            className="px-3 py-2 w-full text-black rounded-lg border border-purple-4/40 focus:ring-2 focus:ring-purple focus:ring-offset-0 focus:outline-none focus:border-transparent"
          />
          <span className="relative right-6 text-purple-6/50">{unit}</span>
        </div>
      </div>
    </div>
  );
});

const ORRange = forwardRef(({
  item, onScreen, onFilter, onValid, renderButtonStartTour,
}, ref) => {
  const { t } = useTranslation();
  const [keySlug] = useState(item.id);
  /*
    WEA-163 - en recherche a l'heure, le meme filtre `price` porte sur le tarif HORAIRE :
    bornes et unite viennent alors de `valuesByHour` (0-1000 €/h, pas de 5) au lieu de
    `values` (0-50000 €, pas de 25). Cote backend c'est le meme parametre `price`, applique
    au tarif horaire precalcule quand `byHour` est vrai (cf. dgs-space SearchService).
  */
  /*
    WEA-163 — dès qu'une date est posée, la fourchette devient un budget total et repasse
    donc aux bornes journalières (0-50000 €), qui couvrent aussi un séjour entier. Les
    bornes horaires ne servent plus qu'au chip sans date.
  */
  // Les modes budget/horaire ne concernent que la fourchette de PRIX (la seule à avoir des
  // bornes horaires) : sans ce garde, poser une date faisait « changer de mode » TOUS les
  // ranges (surface, hauteur...) et la remise à zéro plus bas effaçait leur valeur.
  const isPriceRange = !!item?.valuesByHour;
  const budgetMode = useSearchZustandStore((state) => isPriceRange && !!state.budgetMode);
  const hourlyPricing = useSearchZustandStore((state) => isPriceRange && !!state.hourlyPricing);
  const byHour = !budgetMode && hourlyPricing;
  const params = { ...((byHour && item?.valuesByHour?.[0]) || item?.values?.[0]) };
  const refDetail = useRef();
  const [maxValue, setMaxValue] = useState(params.max);
  /* const [currentValue, setCurrentValue] = useState({
    isChange: null,
    value : [params.min, params.max], validValue: [params.min, params.max]
  }) */

  function extractDataFromUrl(values) {
    if (!values) return [params.min, params.max];
    return values.split('-').map((v) => parseInt(v, 10));
  }

  const [currentValue, setCurrentValue] = useReducer((prev, { value, runSearch, resetBounds }) => {
    let newValue = { ...prev };
    if (value) {
      newValue = { ...newValue, value };
    }
    // WEA-163 - bascule du mode horaire : la valeur validee doit suivre les nouvelles
    // bornes, sinon `getValue()` la compare a des bornes qui ont change et renvoie une
    // fourchette journaliere (0-50000) comme si c'etait une fourchette horaire.
    if (resetBounds) {
      return { ...newValue, validValue: [...newValue.value], runSearch: runSearch || null };
    }
    if (runSearch) {
      newValue = { ...newValue, validValue: [...newValue.value], runSearch: nanoid() };
    }

    if (onFilter) {
      newValue = { ...newValue, validValue: [...newValue.value], runSearch: null };
    }

    return { ...newValue };
  }, {
    isChange: null,
    value: extractDataFromUrl(useSearchZustandStore.getState()?.initParams?.[item.slug]),
    validValue: extractDataFromUrl(useSearchZustandStore.getState()?.initParams?.[item.slug]),
  });

  const [isOpenModal, setIsOpenModal] = useReducer((prev, newValue) => {
    let tmpValue = newValue;
    if (!onScreen) return prev;

    if (tmpValue === 'finalclose') {
      tmpValue = false;
    } else if (tmpValue === 'iconclose' || tmpValue === 'outside') {
      // eslint-disable-line no-empty
    } else if (!tmpValue) {
      if (onValid && !currentValue.runSearch) tmpValue = 'willCloseAndValid';
    }

    return tmpValue;
  }, false);

  function getValue(out) {
    if (currentValue.validValue[0] !== params.min || currentValue.validValue[1] !== params.max) {
      if (out === 'url') return currentValue.validValue.join('-');
      return currentValue.validValue;
    }
    return null;
  }

  function getOutput() {
    return {
      keySlug,
      slug: item.slug,
      url: getValue('url'),
      values: getValue(),
      graphql: {
        [item.slug]: {
          from: currentValue.validValue[0],
          to: currentValue.validValue[1],
        },
      },
    };
  }

  function clearValue(runSearch, e) {
    if (e) e.stopPropagation();
    setCurrentValue({
      isChange: runSearch ? nanoid() : null,
      value: [params.min, params.max],
      runSearch,
    });
    setIsOpenModal(false);
  }

  function isSameDefault() {
    return (currentValue.validValue[0] === params.min && currentValue.validValue[1] === params.max);
  }

  /*
    WEA-163 - le mode « par heure » change les bornes du filtre (0-1000 €/h au lieu de
    0-50000 €). L'etat interne est initialise au montage : sans cette remise a zero, le
    curseur resterait sur l'ancienne echelle. On relance la recherche seulement si une
    fourchette etait active, puisque dans ce cas la valeur envoyee vient de changer.
  */
  /*
    WEA-163 — remise a zero a TOUT changement de nature. Passer de « prix par jour » a
    « budget total » garde l'echelle mais change le sens du chiffre : laisser 0-500 en
    place le transformerait en « 500 € pour tout le sejour », sans que le client ait
    rien touche.
  */
  const priceMode = budgetMode ? 'budget' : (byHour && 'hourly') || 'day';
  // Le mode au montage est déjà le bon : `budgetMode` est restauré depuis l'URL par
  // initNoClientData (searchctx) avant le 1er rendu, comme le filtre HOF. Ce ref ne voit
  // donc que les changements demandés par le client, jamais celui de l'hydratation.
  const refPreviousMode = useRef(priceMode);
  useEffect(() => {
    if (refPreviousMode.current === priceMode) return;
    // Une fourchette etait-elle active AVANT la bascule ? La comparaison doit se faire
    // avec les bornes de l'ancien mode : `params` porte deja les nouvelles.
    const previousParams = {
      ...((refPreviousMode.current === 'hourly' && item?.valuesByHour?.[0]) || item?.values?.[0]),
    };
    const wasFiltering = currentValue.validValue[0] !== previousParams.min
      || currentValue.validValue[1] !== previousParams.max;
    refPreviousMode.current = priceMode;
    setMaxValue(params.max);
    setCurrentValue({
      value: [params.min, params.max],
      resetBounds: true,
      runSearch: wasFiltering ? nanoid() : null,
    });
  }, [priceMode]);

  useImperativeHandle(ref, () => ({
    getValue,
    getOutput,
    clearValue,
    setIsOpenModal,
    isSameDefault,
  }), [currentValue]);

  useEffect(() => {
    if (currentValue.runSearch) {
      if (onValid) onValid();
      /*
      useSearchZustandStore.setState({filterEvent: {
        action:'itemfilterrangechange',
        slug: slugEvent
      }})
      */
    }
  }, [currentValue.runSearch]);

  useEffect(() => {
    refDetail?.current?.setValue([...currentValue.value]);
  }, [currentValue.value]);

  useEffect(() => {
    if (isOpenModal === 'willCloseAndValid') {
      onValid();
      setIsOpenModal('finalclose');
    } else if (isOpenModal === 'iconclose' || isOpenModal === 'outside') {
      onValid(isOpenModal);
      setIsOpenModal('finalclose');
    }
  }, [isOpenModal]);

  const updateMaxValue = ({ newMaxValue, reset }) => {
    // Check if the newMaxValue is greater than the current max value otherwise if reset the maxValue to params.max
    if (reset) setMaxValue(newMaxValue);
    if (newMaxValue > params.max) {
      setMaxValue(newMaxValue);
    }
  };

  function callrunSearch() {
    setCurrentValue({ runSearch: true });
    // reset the maxValue
    updateMaxValue({ newMaxValue: params.max, reset: true });
    setIsOpenModal(false);
  }

  function closeOnlyNotSaveValues(action) {
    setCurrentValue({ value: currentValue.validValue });
    // reset the maxValue
    updateMaxValue({ newMaxValue: params.max, reset: true });
    setIsOpenModal(action);
  }

  function renderResume() {
    return (
      <div className="text-sm flex justify-center items-center transition text-white py-[8px] px-[8px] bg-purple rounded-lg cursor-pointer hover:bg-purple/80">
        <Icon className="mr-2 text-white size-4.5 group-hover:text-white" name={item.slug} />
        <span onClick={() => setIsOpenModal(true)}>
          {`${currentValue.validValue[0]}${params.unit} - ${currentValue.validValue[1]}${params.unit}`}
        </span>
        <button className="w-3" onClick={(e) => { clearValue(true, e); }}><Icon name="close" className="mx-1 w-full text-white fill-current size-1.5" /></button>
      </div>
    );
  }

  /*
    WEA-163 — le titre dit ce que la fourchette compare. Des qu'une date est choisie elle porte
    sur le BUDGET TOTAL de la selection, quelle que soit la nature des dates. Sans date, elle
    reste un prix unitaire journee, ou le tarif horaire si le chip le demande.
    Réservé aux filtres qui ont des bornes horaires (`valuesByHour`), c'est-à-dire au prix.
  */
  const renderFilterTitle = () => {
    if (!item?.valuesByHour) return t(item.title);
    if (budgetMode) return t('search:filter.price.budget');
    if (byHour) return t('search:filter.price.hourly');
    return t(item.title);
  };

  function renderTitle() {
    if (onFilter) return <p className="pb-2 font-medium text-black">{renderFilterTitle()}</p>;
    return (
      <p className="text-sm text-white flex justify-center whitespace-nowrap w-full items-center transition-all duration-200 py-[8px] px-[8px] rounded-lg cursor-pointer bg-purple-6 hover:bg-purple/70 group" onClick={() => setIsOpenModal(true)}>
        <Icon className="mr-2 transition-colors duration-200 size-4.5 text-purple-4/70 group-hover:text-white" name={item.slug} />
        <span className="transition-colors duration-200">{renderFilterTitle()}</span>
        <Icon name="next" className="ml-2 w-2 h-2 transition-colors duration-200 transform rotate-90 text-purple-1 group-hover:text-white" />
      </p>
    );
  }

  function renderContent() {
    return (
      <div className="lg:mt-3 lg:mx-2">
        <Range
          step={params?.step || 1}
          min={params.min}
          max={currentValue?.validValue?.[1] ? (currentValue?.validValue?.[1] > maxValue ? currentValue?.validValue?.[1] : maxValue) : maxValue}
          values={currentValue.value}
          renderTrack={({ props, children }) => (
            <div
              className="px-4 lg:mx-0"
              onMouseDown={props.onMouseDown}
              onTouchStart={props.onTouchStart}
              style={{
                ...props.style,
                height: '36px',
                display: 'flex',
                width: '100%',
              }}
            >
              <div
                ref={props.ref}
                style={{
                  height: '5px',
                  width: '100%',
                  borderRadius: '4px',
                  background: getTrackBackground({
                    values: currentValue.value,
                    colors: ['#d9d9e0', '#120E1A', '#d9d9e0'],
                    min: params.min,
                    max: currentValue?.validValue?.[1] ? (currentValue?.validValue?.[1] > maxValue ? currentValue?.validValue?.[1] : maxValue) : maxValue,
                  }),
                  alignSelf: 'center',
                }}
              >
                {children}
              </div>
            </div>
          )}
          renderThumb={({ props }) => (
            <div className="flex justify-center items-center w-8 h-8 bg-white rounded-full border shadow-button border-white-dark3" {...props}><Icon name="lines" className="mx-1 h-3 fill-current text-purple" /></div>
          )}
          onChange={(values) => {
            setCurrentValue({ isChange: nanoid(), value: [...values] });
          }}
        />
        <RangeDetail
          data={currentValue.value}
          params={params}
          ref={refDetail}
          onChange={(values) => {
            setCurrentValue({ isChange: nanoid(), value: [...values] });
            updateMaxValue({ newMaxValue: values[1] });
          }}
        />
      </div>
    );
  }

  function renderModal() {
    return (
      <>
        <div
          className="absolute top-0 left-0 z-40 w-screen h-screen bg-black opacity-25"
          onClick={() => closeOnlyNotSaveValues('outside')}
        />
        <div className="-mt-[38px]">
          <div className="max-w-[400px] xl:inset-x-auto min-w-[350px] rounded-lg bg-white text-purple-6 z-40 absolute px-6 pt-6 pb-4 shadow-button">
            <h1 className="font-medium text-purple-6">{renderFilterTitle()}</h1>
            <button
              className="absolute top-4 right-4 size-5"
              onClick={() => closeOnlyNotSaveValues('iconclose')}
            >
              <Icon name="close" className="mx-1 fill-current size-4 text-purple-6/30 hover:text-black" />
            </button>
            {renderContent()}
            <div className="flex justify-between items-center mt-5 mb-2">
              <button className="text-base rounded-none border-b text-purple-5/50 border-purple-5/50 hover:text-purple hover:border-purple" onClick={(e) => clearValue(true, e)}>{t('search:clean')}</button>
              <div>
                {renderButtonStartTour?.()}
                <button className="px-6 py-2 purple-gradient" onClick={() => callrunSearch()}>{t('search:save')}</button>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }

  function renderFilter() {
    return (
      <>
        {renderTitle()}
        {renderContent()}
      </>
    );
  }

  return (
    <>
      {!(onScreen && isSameDefault() && !isOpenModal) || renderTitle()}
      {!(onScreen && !isSameDefault() && !isOpenModal) || renderResume()}
      {!(onScreen && !isSameDefault() && isOpenModal) || (
      <>
        {renderResume()}
        {renderModal()}
      </>
      )}
      {!(onScreen && isSameDefault() && isOpenModal) || (
      <>
        {renderTitle()}
        {renderModal()}
      </>
      )}
      {!onFilter || renderFilter()}
    </>
  );
});

export default ORRange;
